/**
 * Message List Component
 * Renders chat messages with formatting support
 */

import React from 'react';
import { User, Bot, Loader2 } from 'lucide-react';

const MessageList = ({
  messages,
  isLoading,
  onSendMessage,
  onResetToMain,
  language
}) => {
  // Format text with markdown-like styling
  const renderFormattedText = (text) => {
    if (!text) return null;
    
    // Split by bold markers (**text**)
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text
        return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      // Check for italic (*text*)
      const italicParts = part.split(/(\*[^*]+\*)/g);
      return italicParts.map((italicPart, i) => {
        if (italicPart.startsWith('*') && italicPart.endsWith('*') && !italicPart.startsWith('**')) {
          return <em key={`${index}-${i}`} className="italic">{italicPart.slice(1, -1)}</em>;
        }
        return <span key={`${index}-${i}`}>{italicPart}</span>;
      });
    });
  };

  return (
    <>
      {/* Messages */}
      {messages.map((message, index) => {
        const isLastBotMessage = message.type === 'bot' && 
          index === messages.length - 1 && 
          !isLoading;

        return (
          <div key={message.id} className={`mb-4 ${message.type === 'user' ? 'text-right' : ''}`}>
            {/* System message (file upload, etc.) */}
            {message.type === 'system' ? (
              <div className="flex justify-center">
                <div className="bg-blue-50 text-blue-700 rounded-lg px-4 py-2 text-sm border border-blue-100">
                  {message.content}
                </div>
              </div>
            ) : (
            <div className={`inline-flex items-start gap-2 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${message.type === 'user' ? 'bg-primary text-white' : 'bg-slate-100'}`}>
                {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4 text-slate-600" />}
              </div>
              <div className={`rounded-lg px-4 py-3 ${message.type === 'user' ? 'bg-primary text-white' : message.isError ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-700'}`}>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{renderFormattedText(message.content)}</div>
              </div>
            </div>
            )}

            {/* Follow-up suggestions for bot messages */}
            {isLastBotMessage && !message.isError && (
              <div className="mt-3 ml-10">
                {/* Info request flow buttons */}
                {message.awaitingInfoRequest ? (
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      onClick={() => onSendMessage(language === 'pt' ? "Sim, solicitar informação" : "Yes, request information")}
                      className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full hover:bg-emerald-200 transition-colors font-medium"
                      data-testid="info-request-yes"
                    >
                      {language === 'pt' ? '✓ Sim, solicitar' : '✓ Yes, request'}
                    </button>
                    <button
                      onClick={() => onSendMessage(language === 'pt' ? "Não, tenho outras questões" : "No, I have other questions")}
                      className="text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-200 transition-colors"
                      data-testid="info-request-no"
                    >
                      {language === 'pt' ? '✗ Não, tenho outras questões' : '✗ No, I have other questions'}
                    </button>
                  </div>
                ) : message.awaitingEmail ? (
                  <div className="text-xs text-slate-500 italic">
                    {language === 'pt' ? 'Por favor, introduza o seu email acima.' : 'Please enter your email above.'}
                  </div>
                ) : (
                  <>
                    {/* Primary actions - Hide when pilot version message is shown */}
                    {!message.content?.includes('versão piloto') && !message.content?.includes('pilot version') && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          onClick={() => onSendMessage(language === 'pt' ? "Sim, aprofundar esta análise" : "Yes, go deeper")}
                          className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full hover:bg-primary/20 transition-colors font-medium"
                          data-testid="follow-up-deeper"
                        >
                          {language === 'pt' ? '↓ Aprofundar' : '↓ Go deeper'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Reset to main menu (level 1) without sending a message
                            if (onResetToMain) {
                              onResetToMain();
                            }
                          }}
                          className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full hover:bg-emerald-200 transition-colors font-medium"
                          data-testid="follow-up-enough"
                        >
                          {language === 'pt' ? '✓ Suficiente' : '✓ Enough'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Loading indicator */}
      {isLoading && (
        <div className="mb-4">
          <div className="inline-flex items-start gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-slate-100">
              <Bot className="w-4 h-4 text-slate-600" />
            </div>
            <div className="rounded-lg px-4 py-3 bg-slate-50">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{language === 'pt' ? 'A analisar...' : 'Analyzing...'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageList;
